import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  AddPageForm,
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  PagesList,
} from '@components';
import { usePageTemplates } from '@hooks';
import { businessQuery, pagesQuery, pageMutation, siteQuery } from '@queries';
import { ChevronRightIcon } from '@svgs';

import { TemplateBreadcrumbs } from '../components/TemplateBreadcrumbs/TemplateBreadcrumbs.jsx';
import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

export const TemplateAddPage = () => {
  const { t, i18n } = useTranslation();
  const { businessID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // get business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // get site/template
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  const { data: pageTemplates, isPending: pageTemplatesIsPending } =
    usePageTemplates(siteID, siteData?.theme_id);

  // save datamodel
  const mutation = useMutation(
    pageMutation(null, {
      onSuccess: (data) => {
        // invalidate list views so they are re-fetched
        queryClient.invalidateQueries({ queryKey: ['pages', 'list'] });
        if (data.type == 'template_page') {
          // navigate to the edit view
          navigate(
            `/dashboard/projects/${businessID}/templates/${siteID}/pages/${data.id}`,
          );
        } else {
          // navigate to the pages list
          navigate(
            `/dashboard/projects/${businessID}/templates/${siteID}/pages`,
          );
        }
      },
    }),
  );

  const isPending = siteIsPending || pageTemplatesIsPending;

  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <TemplateBreadcrumbs businessData={businessData} siteData={siteData}>
            <li className="sb-flex">
              <Link
                to={`/dashboard/projects/${businessID}/templates/${siteID}/pages`}
              >
                Pages
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              <span>{t('Add')}</span>
            </li>
          </TemplateBreadcrumbs>
          <div className="sb-manage-content">
            <h1>{t('Add')}</h1>
            {isPending && <Loader className="sb-loader sb-loader-large" />}
            {pageTemplates && siteData && (
              <AddPageForm
                disabled={mutation.isPending}
                mode="template"
                mutation={mutation}
                pageTemplates={pageTemplates}
                siteData={siteData}
                onCancel={() => {
                  navigate(
                    `/dashboard/projects/${businessID}/templates/${siteID}/pages`,
                  );
                }}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
